import { useState } from "react";
import { Check, Copy, Link2Off, MessageCircle, Share2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

export default function ShareButton({ book, onChange, className = "btn-ghost !px-4 !py-2" }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [copied, setCopied] = useState(false);
  const token = book?.share_token;
  const url = token ? `${window.location.origin}/s/${token}` : "";
  const waText = encodeURIComponent(`Flip through our photo book "${book?.title}" — ${url}`);

  const create = async () => {
    setBusy(true);
    try { const r = await api.post(`/books/${book.id}/share`); onChange?.(r.data.share_token); toast.success("Private link is live"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const revoke = async () => {
    setBusy(true);
    try { await api.delete(`/books/${book.id}/share`); onChange?.(null); toast("Link turned off — nobody can open it now"); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Couldn't copy — long-press the link instead"); }
  };

  return (
    <>
      <button type="button" data-testid="share-book-btn" onClick={() => setOpen(true)} className={className}>
        <Share2 className="h-4 w-4" />Share
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="share-dialog" className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Share with family</DialogTitle>
            <DialogDescription>Anyone with the link can flip through the book — view only, no login needed. You can turn it off any time.</DialogDescription>
          </DialogHeader>
          {!token ? (
            <button data-testid="share-create-link-btn" onClick={create} disabled={busy} className="btn-primary w-full">
              <Share2 className="h-4 w-4" />Create private link
            </button>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center gap-2 rounded-lg border border-[#E8E2D9] bg-[#FDFBF7] p-2 pl-3">
                <input data-testid="share-link-input" readOnly value={url} onFocus={(e) => e.target.select()} className="min-w-0 flex-1 bg-transparent font-mono text-xs text-[#5E5868] outline-none" />
                <button data-testid="share-copy-btn" onClick={copy} className="btn-ghost !px-3 !py-1.5 !text-xs">
                  {copied ? <Check className="h-3.5 w-3.5 text-[#789A82]" /> : <Copy className="h-3.5 w-3.5" />}{copied ? "Copied" : "Copy"}
                </button>
              </div>
              <a data-testid="share-whatsapp-btn" href={`https://wa.me/?text=${waText}`} target="_blank" rel="noreferrer"
                className="btn-primary w-full !bg-[#25D366] !shadow-[0_6px_20px_-8px_rgba(37,211,102,0.7)] hover:!bg-[#1EBE5B]">
                <MessageCircle className="h-4 w-4" />Send on WhatsApp
              </a>
              <button data-testid="share-revoke-btn" onClick={revoke} disabled={busy} className="btn-ghost w-full !text-rose-700">
                <Link2Off className="h-4 w-4" />Turn off link
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
